import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";

import { NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { AuthorizationService } from '@myapp/modules/fwcore/services';
import { CheckBoxModule } from "@progress/kendo-angular-inputs";
import { ActionName, ListBoxComponent, ListBoxModule, ListBoxSelectionEvent } from "@progress/kendo-angular-listbox";
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { RequestUserPermission } from "../../../../../modules/fwcore";
import { Module, ModuleChecked } from "../../../models";
import { RequestModule } from "../../../models/messages";
import { AdminService } from '../../../services';

@Component({
  selector: 'app-modules-list',
  templateUrl: './modules-list.component.html',
  styleUrls: ['./modules-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [ListBoxModule, NgIf, FormsModule, CheckBoxModule]
})

export class ModulesListComponent implements AfterViewInit, OnChanges, OnDestroy {

  /*-------------------------- Input / Outputs --------------------------------*/

  @Input() public licenseId: string | null = null;
  @Input() public userId: string | null = null;
  @Input() public hasApi: boolean | null = null;
  @Input() public active: boolean | null = null;
  @Input() public includeHost: boolean | null = null;
  @Input() public showCheckbox: boolean = false;

  @Input() public minHeight: number = 200;
  @Input() public widthThreshold: number = 600;

  @Input()
  set checkedIds(ids: string[]) {
    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }
  get checkedIds(): string[] {

    const checkedItemIds: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.id!);

    return checkedItemIds ?? [];
  }

  @Output() selected: EventEmitter<Module | null> = new EventEmitter();
  @Output() dataLoaded: EventEmitter<number | null> = new EventEmitter();

  /*-------------------------- public properties / methods --------------------*/

  public selectedItem?: Module | null;

  public heightList: number = 200;

  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();
    let boundClientRect = this.elem.nativeElement.getBoundingClientRect()
    ctrlOptions.elemTop = boundClientRect.top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    if (window.innerWidth > this.widthThreshold)
      this.heightList = this.controlResizer.calculateHeight(ctrlOptions);
    else {
      // Do not dynamically resize height if below the width threshold
      this.heightList = this.minHeight;
    }
  }

  public Initialized(): void {

    this.hasInitialized = true;
    this.getModules();
  }

  public clearSelection() {
    this.selectedItem = null;
    this.listbox.clearSelection();
  }

  /*-------------------------- Internal properties  ---------------------------*/

  @ViewChild(ListBoxComponent, { static: false })
  protected listbox!: ListBoxComponent;

  protected dataItems: ModuleChecked[] = [];

  protected loading: boolean = true;

  protected hasInitialized: boolean = false;

  protected destroy$ = new Subject();

  protected reqPerm: RequestUserPermission = {

  }

  /*-------------------------- Constructor  --------------------------*/

  constructor(
    public serviceAuth: AuthorizationService
    , protected router: Router
    , protected adminService: AdminService
    , protected controlResizer: ControlResizerService
    , protected elem: ElementRef
  ) {

  }

  /*----------------------------------- ng  --------------------------*/

  ngAfterViewInit(): void {
    this.reqPerm.route = this.router.url;
  }

  ngOnChanges(changes: SimpleChanges) {

    if (this.hasInitialized && this.inputChanges(changes)) {
      this.getModules();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/

  public onActionClick(event: ActionName): void {
    const index: number = this.listbox.selectedIndex;
  }

  public onSelectionEvent(event: ListBoxSelectionEvent): void {

    this.setSelectedItem(event.index);
  }

  /*--------------------------- internal functions  --------------------------*/


  protected inputChanges(changes: SimpleChanges): boolean {

    let update: boolean = false;

    if (changes.licenseId && !changes.licenseId.firstChange)
      return true;

    return update;
  }

  protected getModules(): void {

    this.loading = true;

    const req: RequestModule = {
      licenseId: this.licenseId
      , active: this.active
      , hasApi: this.hasApi
      , includeHost: this.includeHost
    };

    this.adminService.getModules$(req)
      .pipe(takeUntil(this.destroy$), tap(() => this.loading = false))
      .subscribe(
        resp => {

          this.dataItems = resp.data!;
          this.dataLoaded.emit(this.dataItems?.length);

          // by default select the first item
          if (this.dataItems.length > 0) {
            this.listbox.selectItem(0);
            this.selectedItem = this.dataItems[0];
          }
          else
            this.selectedItem = null;

          this.selected.emit(this.selectedItem);
        }
      );

  }

  protected setSelectedItem(index: number) {
    this.selectedItem = this.listbox.data[index];
    this.selected.emit(this.selectedItem);
  }
}
